"use client";

import { useEffect, useState } from "react";

import NavCoach from "@/components/nav/NavCoach";
import PlaybookChatAnswer from "@/components/playbook/PlaybookChatAnswer";
import { SEARCH_PLAYBOOK_TOOL } from "@/lib/playbook/chat-messages";
import AgentActionLinks from "@/components/agent-actions/AgentActionLinks";
import { getChatErrorMessage, useChat } from "@/hooks/useChatAdapter";
import { useSabineChatPersistence } from "@/hooks/useSabineChatPersistence";
import { newHomeThreadContextId } from "@/lib/sabine-chat/home-threads";
import { SABINE_SURFACES } from "@/lib/sabine/surfaces";

// The tool parts whose output carries playable resources for the chat's player:
// a playbook search's matches, or a resource listing.
const RESOURCE_TOOL_PARTS = new Set([`tool-${SEARCH_PLAYBOOK_TOOL}`, "tool-salesably_list_studio_content", "tool-get_playbook_resources"]);

// The route records every turn under the same row and the user message's id,
// so a vote in the chat updates that row instead of creating a second one.
const { source: FEEDBACK_SOURCE, contextType: FEEDBACK_CONTEXT_TYPE } =
  SABINE_SURFACES.home.feedback;

/**
 * Sabine on the home page: the "ask anything" chat, open beside the rail from
 * the moment the page loads. Streams from /api/home/sabine, grounded in the
 * playbook, recent practice, learning paths and due work. Each chat is its
 * own saved thread in this studio (lib/sabine-chat/home-threads.js), so
 * switching studios never shows a thread about another studio's team, and
 * "New chat" in the header starts another while the last one stays in Chats.
 *
 * @param {object} props
 * @param {string} props.userId
 * @param {string} props.workspaceId
 * @param {string} props.threadId      The thread to open (its contextId)
 * @param {string[]} props.prompts     The suggested questions for this view
 * @param {string} props.placeholder  What the composer invites
 */
export default function HomeCoach({ userId, workspaceId, threadId, prompts, placeholder }) {
  const [contextId, setContextId] = useState(threadId);
  // A chat link (?chat=) names another thread while Home stays mounted.
  useEffect(() => setContextId(threadId), [threadId]);

  const {
    messages,
    input,
    setInput,
    handleInputChange,
    isLoading,
    sendMessage,
    setMessages,
    error,
  } = useChat({
    api: "/api/home/sabine",
    initialMessages: [],
    // The route leaves the thread under way out of what Sabine recalls.
    body: () => ({ threadId: contextId }),
  });

  useSabineChatPersistence({
    source: FEEDBACK_SOURCE,
    contextId,
    surface: "home",
    messages,
    setMessages,
    enabled: Boolean(userId && contextId),
  });

  // The composer also hands over `{ enableWebSearch }`; the home route has
  // no web search, so it is not read.
  const send = () => {
    const value = input.trim();
    if (!value || isLoading) return;
    setInput("");
    sendMessage({ role: "user", content: value }).catch((error) => {
      console.error("Failed to send Sabine message", error);
    });
  };

  return (
    <NavCoach
      defaultOpen
      placeholder={placeholder}
      messages={messages}
      isProcessing={isLoading}
      inputValue={input}
      onInputChange={handleInputChange}
      setInputValue={setInput}
      onSubmit={send}
      error={getChatErrorMessage(error)}
      prompts={prompts}
      onPromptSelect={setInput}
      onReset={() => setContextId(newHomeThreadContextId(workspaceId))}
      subject={{
        source: FEEDBACK_SOURCE,
        contextType: FEEDBACK_CONTEXT_TYPE,
        workspaceId,
      }}
      renderMessageContent={(message) => (
        <>
          <PlaybookChatAnswer
            content={message.content}
            resources={(message.parts || []).flatMap((part) =>
              part.state === "output-available" && RESOURCE_TOOL_PARTS.has(part.type)
                ? part.output?.data?.resources || part.output?.data?.items || part.output?.items || []
                : [],
            )}
          />
          <AgentActionLinks content={message.content || ""} className="mt-3" />
        </>
      )}
    />
  );
}
